import { Button } from '@doltech/ui/lib/figma/ButtonV2/ButtonV2';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Box } from '@doltech/ui/lib/figma/Common/BoxV2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { FontSizeElementVariants } from '@doltech/ui/lib/figma/DolEditor/decorators/font-size-switcher/components/FontSizeSwitcherElement.types';
import { FontSizeSwitcherPlugin } from '@doltech/ui/lib/figma/DolEditor/decorators/font-size-switcher/FontSizeSwitcherPlugin';
import { FontSizeModeEnum } from '@doltech/ui/lib/figma/DolEditor/decorators/font-size-switcher/types';
import { DolEditor } from '@doltech/ui/lib/figma/DolEditor/DolEditor';
import { ReadOnlyPlugin } from '@doltech/ui/lib/figma/DolEditor/handlers/read-only/ReadOnlyPlugin';
import {
  SectionOfContent,
  SectionOfContentProps,
} from '@doltech/ui/lib/figma/Layout/Landing/Content/SectionOfContent';
import { Separator } from '@doltech/ui/lib/figma/Layout/Landing/Content/Separator';
import { Link } from '@doltech/ui/lib/figma/Link';
import { deviceBreakpoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { BoldPlugin } from '@doltech/ui/lib/slatePlugins/marks/bold/BoldPlugin';
import { HighlightPlugin } from '@doltech/ui/lib/slatePlugins/marks/highlight/HighlightPlugin';
import { UnderlinePlugin } from '@doltech/ui/lib/slatePlugins/marks/underline/UnderlinePlugin';
import { SkillEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { getLandingUrlFromPath } from '@doltech/utils/lib/url';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { useLandingSeoPageDataStore } from '../landing-home/model/landingPageData.store';
import { AnswerKeysTrack } from '../view-solution/components/AnswerKeysTrack';
import { useViewSolutionStore } from '../view-solution/model/view-solution.hook';
import { GroupQuestionsSubject } from './components/TestContent';
import { useViewTestSubjectStore } from './model/view-test-subject.hook';
import withHydrationOnDemand from '@doltech/core/lib/hooks/withDolHydrationOnDemand';

export const ContentLayout = styled.div`
  .editor-passage {
    pointer-events: none;
    .element-type-${FontSizeElementVariants.H1},.element-type-${FontSizeElementVariants.H2} {
      text-align: center;
    }
  }
`;

export const SectionHeader = ({ index, skill }) => {
  const sectionKey = SkillEnum.READING === skill ? 'Passage' : 'Section';
  return (
    <Box p="0 16px" mb="16px">
      <Typography.Title level={2} id={`${sectionKey}-${index}`} variant="semi-bold/32">
        {sectionKey}
      </Typography.Title>
    </Box>
  );
};

export const ButtonDoTestSection = ({ doTestLink }) => {
  return (
    <Box p={['0 16px', 0]} mt="24px">
      <Link target="_blank" href={doTestLink}>
        <Button.Primary fullWidth>Làm bài ngay</Button.Primary>
      </Link>
    </Box>
  );
};

interface ViewSolutionSectionProps extends Pick<SectionOfContentProps, 'renderTitle'> {
  sectionIndex: number;
}

const ExpandCollapseHeader = styled.div`
  padding: 16px 24px;
  background-color: ${colorsV2.gray5};
  box-shadow: 0 -1px 0 0 ${colorsV2.gray10} inset;
  border-radius: 0;
  display: grid;
  grid-template-columns: 1fr max-content;
  align-items: center;
  cursor: pointer;

  .soc-title {
    padding: 0;
  }

  @media ${deviceBreakpoints.fromTablet} {
    border-radius: 16px 16px 0 0;
  }
`;

export const ViewSolutionSection = withHydrationOnDemand({ on: ['idle'] })(
  observer(({ sectionIndex, renderTitle }: ViewSolutionSectionProps) => {
    const solutionStore = useViewSolutionStore();
    const store = useViewTestSubjectStore();
    const tableOfContent = store.getTableOfContent();
    const landingPageDataStore = useLandingSeoPageDataStore();

    const explanationLink = landingPageDataStore.getExplanationLink();

    const singleAnswerKeys = [
      solutionStore.getAnswerKeysByTestSectionIndex(sectionIndex, explanationLink)?.[0],
    ];

    return (
      <SectionOfContent
        className="test-section-content"
        id={`solution-passage-${sectionIndex + 1}`}
        sectionId={`solution-passage-${sectionIndex + 1}`}
        tableOfContent={tableOfContent}
        renderTitle={(title) => <ExpandCollapseHeader>{renderTitle?.(title)}</ExpandCollapseHeader>}
      >
        <SectionOfContent.Content>
          <AnswerKeysTrack data={singleAnswerKeys} />
        </SectionOfContent.Content>
      </SectionOfContent>
    );
  })
);

const ViewEditorSection = withHydrationOnDemand({
  on: ['idle'],
})(({ index, tableOfContent, renderTitle, passage }) => {
  return (
    <SectionOfContent
      className="test-section-content"
      id={`test-section-${index}`}
      sectionId={`test-section-${index}`}
      tableOfContent={tableOfContent}
      renderTitle={renderTitle}
    >
      <SectionOfContent.Content>
        <DolEditor
          className="editor-passage"
          initialValue={passage}
          plugins={editorPlugins}
          readOnly
        />
      </SectionOfContent.Content>
    </SectionOfContent>
  );
});

export const editorPlugins = [
  ReadOnlyPlugin(),
  BoldPlugin(),
  UnderlinePlugin(),
  HighlightPlugin(),
  FontSizeSwitcherPlugin(FontSizeModeEnum.DEFAULT),
];

interface SubjectReadingContentProps {}

export const SubjectReadingContent = observer((props: SubjectReadingContentProps) => {
  const store = useViewTestSubjectStore();
  const tableOfContent = store.getTableOfContent();
  const landingSeoPageDataStore = useLandingSeoPageDataStore();
  const doTestLink = landingSeoPageDataStore.getDoTestLink();

  const isFullTest = TestTypeEnum.FULL_TEST === store.testType;
  const fontVariant = isFullTest ? 'medium/24' : 'semi-bold/32';

  const renderTitle = React.useCallback(
    (title) => (
      <Typography.Title className="font-inter" variant={fontVariant} level={isFullTest ? 3 : 2}>
        {title}
      </Typography.Title>
    ),
    [fontVariant, isFullTest]
  );

  return (
    <ContentLayout>
      {store.testSections.map((testSection, testSectionIndex) => {
        const index = testSectionIndex + 1;

        return (
          <div className="test-section-container" key={index}>
            {isFullTest && <SectionHeader index={index} skill={SkillEnum.READING} />}

            <ViewEditorSection
              index={index}
              tableOfContent={tableOfContent}
              renderTitle={renderTitle}
              passage={testSection?.getPassage()}
            />

            <Separator mb="24px" variant="three-dot-horizontal" />

            <SectionOfContent
              className="test-section-content"
              id={`question-passage-${index}`}
              sectionId={`question-passage-${index}`}
              tableOfContent={tableOfContent}
              renderTitle={renderTitle}
            >
              <SectionOfContent.Content>
                {testSection.questionGroups.map((group) => {
                  return (
                    <GroupQuestionsSubject
                      key={group.id}
                      currentSection={testSection}
                      group={group}
                    />
                  );
                })}
              </SectionOfContent.Content>
            </SectionOfContent>

            <ButtonDoTestSection doTestLink={getLandingUrlFromPath(doTestLink)} />

            <Separator my="24px" variant="three-dot-horizontal" />

            <ViewSolutionSection sectionIndex={testSectionIndex} renderTitle={renderTitle} />

            <Separator my="24px" variant="three-dot-horizontal" />
          </div>
        );
      })}
    </ContentLayout>
  );
});
